import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { BACKEND_URL } from '../../utils/api';
import LeadCRMPanel from './LeadCRMPanel';
import SpanishDripStats from './SpanishDripStats';
import {
  Users, Mail, Phone, Building2, Search, Filter,
  ChevronDown, ChevronUp, ExternalLink, Clock,
  MessageSquare, Globe, Zap, FileText, Megaphone,
  MousePointer, BarChart3, RefreshCcw, Flame, Thermometer, Snowflake, Trash2,
  Send, Loader2,
} from 'lucide-react';

const API = `${BACKEND_URL}/api`;

const TYPE_CONFIG = {
  'Demo Request':     { color: 'bg-blue-600',    icon: Globe },
  'Contact Form':     { color: 'bg-slate-600',   icon: Mail },
  'Consultation':     { color: 'bg-purple-600',  icon: MessageSquare },
  'Local Business':   { color: 'bg-emerald-600', icon: Building2 },
  'App Mockup':       { color: 'bg-orange-600',  icon: FileText },
  'Website Analysis': { color: 'bg-cyan-600',    icon: BarChart3 },
  'Exit Popup':       { color: 'bg-pink-600',    icon: MousePointer },
  'PPC Audit':        { color: 'bg-amber-600',   icon: Megaphone },
  'Widget Lead':      { color: 'bg-teal-600',    icon: Zap },
};

const TEMP_CONFIG = {
  hot:  { icon: Flame,      color: 'text-red-500' },
  warm: { icon: Thermometer, color: 'text-orange-500' },
  cold: { icon: Snowflake,   color: 'text-blue-500' },
  dead: { icon: Trash2,      color: 'text-slate-400' },
};

const STATUS_COLORS = {
  new: 'bg-green-100 text-green-800 border-green-200',
  contacted: 'bg-blue-100 text-blue-800 border-blue-200',
  qualified: 'bg-purple-100 text-purple-800 border-purple-200',
  converted: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  lost: 'bg-red-100 text-red-800 border-red-200',
};

const STATUS_OPTIONS = ['new', 'contacted', 'qualified', 'converted', 'lost'];

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

export default function AdminLeadsManager({ getAuthHeaders }) {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_type: {}, by_status: {}, by_language: { en: 0, es: 0 } });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [langFilter, setLangFilter] = useState('all');
  const [sortField, setSortField] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [expandedId, setExpandedId] = useState(null);
  const [outreachSending, setOutreachSending] = useState(false);
  const [outreachResult, setOutreachResult] = useState(null);

  useEffect(() => { fetchLeads(); }, []);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/leads/unified`, { headers: getAuthHeaders() });
      setLeads(res.data.leads || []);
      setStats(res.data.stats || { total: 0, by_type: {}, by_status: {}, by_language: { en: 0, es: 0 } });
    } catch (err) {
      console.error('Failed to fetch leads:', err);
    }
    setLoading(false);
  };

  const updateStatus = async (lead, newStatus) => {
    try {
      await axios.put(`${API}/admin/leads/unified/${lead.id}/status`, {
        collection: lead.collection,
        status: newStatus,
      }, { headers: getAuthHeaders() });
      setLeads(prev => prev.map(l => l.id === lead.id ? { ...l, status: newStatus } : l));
      setStats(prev => {
        const ns = { ...prev.by_status };
        ns[lead.status] = Math.max(0, (ns[lead.status] || 1) - 1);
        ns[newStatus] = (ns[newStatus] || 0) + 1;
        return { ...prev, by_status: ns };
      });
    } catch {
      alert('Failed to update status');
    }
  };

  const filtered = useMemo(() => {
    let result = leads;
    if (typeFilter !== 'all') result = result.filter(l => l.type === typeFilter);
    if (statusFilter !== 'all') result = result.filter(l => l.status === statusFilter);
    if (langFilter !== 'all') result = result.filter(l => (l.preferred_language || 'en') === langFilter);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(l =>
        (l.name || '').toLowerCase().includes(q) ||
        (l.email || '').toLowerCase().includes(q) ||
        (l.business || '').toLowerCase().includes(q) ||
        (l.phone || '').toLowerCase().includes(q)
      );
    }
    result.sort((a, b) => {
      const aVal = a[sortField] || '';
      const bVal = b[sortField] || '';
      return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });
    return result;
  }, [leads, typeFilter, statusFilter, langFilter, search, sortField, sortDir]);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };
  const SortIcon = ({ field }) => sortField === field ? (sortDir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />) : null;

  const leadTypes = [...new Set(leads.map(l => l.type))];

  const sendSpanishOutreach = async () => {
    const esLeads = filtered.filter(l => (l.preferred_language || 'en') === 'es');
    if (!esLeads.length) {
      alert('No Spanish leads in the current filtered view.');
      return;
    }
    const confirmed = window.confirm(
      `Send Spanish welcome outreach to ${esLeads.length} lead(s)?\n\n` +
      `• Each lead receives one bilingual welcome email in Spanish\n` +
      `• Already-contacted leads via this template are skipped\n` +
      `• Status will flip to "contacted" for successful sends`
    );
    if (!confirmed) return;
    setOutreachSending(true);
    setOutreachResult(null);
    try {
      const res = await axios.post(`${API}/admin/leads/send-spanish-outreach`, {
        leads: esLeads.map(l => ({
          id: l.id,
          collection: l.collection,
          email: l.email,
          name: l.name,
        })),
        language: 'es',
        template: 'welcome',
      }, { headers: getAuthHeaders ? getAuthHeaders() : { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } });
      setOutreachResult(res.data);
      // Refresh leads to show updated statuses
      try {
        const refreshed = await axios.get(`${API}/admin/leads/unified`, {
          headers: getAuthHeaders ? getAuthHeaders() : { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
        });
        setLeads(refreshed.data.leads || []);
        setStats(refreshed.data.stats || stats);
      } catch { /* non-fatal */ }
    } catch (e) {
      alert('Outreach failed: ' + (e?.response?.data?.detail || e.message));
    }
    setOutreachSending(false);
  };

  const startSpanishDrip = async () => {
    const esLeads = filtered.filter(l => (l.preferred_language || 'en') === 'es');
    if (!esLeads.length) {
      alert('No Spanish leads in the current filtered view.');
      return;
    }
    const confirmed = window.confirm(
      `Enroll ${esLeads.length} Spanish lead(s) in the 4-stage drip campaign?\n\n` +
      `• Day 0: Welcome\n` +
      `• Day 3: Benefits\n` +
      `• Day 7: Carlos testimonial\n` +
      `• Day 14: Final CTA\n\n` +
      `Already-enrolled leads are skipped. Stops automatically on conversion or unsubscribe.`
    );
    if (!confirmed) return;
    setOutreachSending(true);
    setOutreachResult(null);
    try {
      const res = await axios.post(`${API}/admin/leads/start-spanish-drip`, {
        leads: esLeads.map(l => ({
          id: l.id,
          collection: l.collection,
          email: l.email,
          name: l.name,
        })),
        language: 'es',
        template: 'drip_4stage',
      }, { headers: getAuthHeaders ? getAuthHeaders() : { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } });
      setOutreachResult({
        sent: res.data.enrolled,
        skipped: res.data.already_enrolled_or_skipped,
        failed: res.data.no_email,
        drip: true,
      });
    } catch (e) {
      alert('Drip enrollment failed: ' + (e?.response?.data?.detail || e.message));
    }
    setOutreachSending(false);
  };

  return (
    <div className="space-y-5" data-testid="admin-leads-crm">
      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Card className="bg-gradient-to-br from-slate-800 to-slate-900 border-slate-700">
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-2xl font-bold text-white" data-testid="leads-total">{stats.total}</p>
            <p className="text-xs text-slate-400 mt-0.5">Total Leads</p>
          </CardContent>
        </Card>
        {STATUS_OPTIONS.map(s => (
          <Card key={s} className={`cursor-pointer transition-all ${statusFilter === s ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => setStatusFilter(statusFilter === s ? 'all' : s)}>
            <CardContent className="pt-4 pb-3 px-4">
              <p className="text-2xl font-bold" data-testid={`leads-${s}`}>{stats.by_status?.[s] || 0}</p>
              <p className="text-xs text-slate-500 mt-0.5 capitalize">{s}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Type Chips */}
      <div className="flex flex-wrap items-center gap-2">
        <Badge
          className={`cursor-pointer text-xs px-3 py-1 ${typeFilter === 'all' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
          onClick={() => setTypeFilter('all')}
          data-testid="filter-all"
        >All ({stats.total})</Badge>
        {leadTypes.map(t => {
          const cfg = TYPE_CONFIG[t] || { color: 'bg-slate-600' };
          const count = stats.by_type?.[t] || 0;
          return (
            <Badge key={t}
              className={`cursor-pointer text-xs px-3 py-1 ${typeFilter === t ? cfg.color + ' text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
              onClick={() => setTypeFilter(typeFilter === t ? 'all' : t)}
              data-testid={`filter-${t.toLowerCase().replace(/\s/g,'-')}`}
            >{t} ({count})</Badge>
          );
        })}
        <span className="mx-2 text-slate-300">|</span>
        <Badge
          className={`cursor-pointer text-xs px-3 py-1 ${langFilter === 'all' ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          onClick={() => setLangFilter('all')}
          data-testid="filter-lang-all"
          title="Show leads in any language"
        >🌐 All ({(stats.by_language?.en || 0) + (stats.by_language?.es || 0)})</Badge>
        <Badge
          className={`cursor-pointer text-xs px-3 py-1 ${langFilter === 'en' ? 'bg-blue-700 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          onClick={() => setLangFilter(langFilter === 'en' ? 'all' : 'en')}
          data-testid="filter-lang-en"
          title="English-speaking leads"
        >🇺🇸 EN ({stats.by_language?.en || 0})</Badge>
        <Badge
          className={`cursor-pointer text-xs px-3 py-1 ${langFilter === 'es' ? 'bg-orange-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          onClick={() => setLangFilter(langFilter === 'es' ? 'all' : 'es')}
          data-testid="filter-lang-es"
          title="Spanish-speaking leads"
        >🇪🇸 ES ({stats.by_language?.es || 0})</Badge>
        {langFilter === 'es' && filtered.some(l => (l.preferred_language || 'en') === 'es') && (
          <>
            <Button
              size="sm"
              onClick={sendSpanishOutreach}
              disabled={outreachSending}
              className="ml-auto bg-orange-600 hover:bg-orange-700 text-white text-xs px-3 py-1 h-7"
              data-testid="btn-send-spanish-outreach"
            >
              {outreachSending ? (
                <><Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> Enviando...</>
              ) : (
                <><Send className="h-3 w-3 mr-1.5" /> Send ES Welcome ({filtered.filter(l => (l.preferred_language || 'en') === 'es').length})</>
              )}
            </Button>
            <Button
              size="sm"
              onClick={startSpanishDrip}
              disabled={outreachSending}
              className="bg-purple-600 hover:bg-purple-700 text-white text-xs px-3 py-1 h-7"
              data-testid="btn-start-spanish-drip"
              title="Enroll in 4-stage automated drip (Day 0 → Day 3 → Day 7 → Day 14)"
            >
              <Send className="h-3 w-3 mr-1.5" /> Start ES Drip ({filtered.filter(l => (l.preferred_language || 'en') === 'es').length})
            </Button>
          </>
        )}
      </div>

      {outreachResult && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 text-sm text-emerald-800 flex items-center justify-between" data-testid="outreach-result">
          <span>
            {outreachResult.drip ? (
              <>✅ Drip enrolled: <strong>{outreachResult.sent}</strong> new · <strong>{outreachResult.skipped}</strong> already enrolled · <strong>{outreachResult.failed}</strong> no email — Day 0 sends within 4 hours</>
            ) : (
              <>✅ Spanish outreach: <strong>{outreachResult.sent}</strong> sent · <strong>{outreachResult.skipped}</strong> skipped (already contacted) · <strong>{outreachResult.failed}</strong> failed</>
            )}
          </span>
          <button onClick={() => setOutreachResult(null)} className="text-emerald-600 hover:text-emerald-900 ml-3">✕</button>
        </div>
      )}

      {/* Spanish Drip Stats Panel — only visible when ES filter is active */}
      {langFilter === 'es' && (
        <SpanishDripStats getAuthHeaders={getAuthHeaders} />
      )}

      {/* Search & Controls */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by name, email, business, phone..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 h-9 text-sm"
            data-testid="leads-search"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="border rounded-lg px-3 py-1.5 text-sm bg-white"
            data-testid="status-filter"
          >
            <option value="all">All Statuses</option>
            {STATUS_OPTIONS.map(s => <option key={s} value={s} className="capitalize">{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>
          <Button onClick={fetchLeads} variant="outline" size="sm" className="h-9" data-testid="leads-refresh">
            <RefreshCcw className="h-3.5 w-3.5 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Leads Table */}
      <Card>
        <CardContent className="pt-0 pb-2 px-0">
          {loading ? (
            <div className="text-center py-12 text-slate-500">Loading leads...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Users className="h-10 w-10 mx-auto mb-3 text-slate-300" />
              <p>No leads found</p>
              <p className="text-xs mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left p-3 font-semibold text-xs text-slate-500">TYPE</th>
                    <th className="text-left p-3 font-semibold text-xs text-slate-500 cursor-pointer select-none" onClick={() => toggleSort('name')}>
                      <span className="flex items-center gap-1">LEAD <SortIcon field="name" /></span>
                    </th>
                    <th className="text-left p-3 font-semibold text-xs text-slate-500">CONTACT</th>
                    <th className="text-left p-3 font-semibold text-xs text-slate-500 cursor-pointer select-none" onClick={() => toggleSort('date')}>
                      <span className="flex items-center gap-1">DATE <SortIcon field="date" /></span>
                    </th>
                    <th className="text-left p-3 font-semibold text-xs text-slate-500 cursor-pointer select-none" onClick={() => toggleSort('status')}>
                      <span className="flex items-center gap-1">STATUS <SortIcon field="status" /></span>
                    </th>
                    <th className="text-left p-3 font-semibold text-xs text-slate-500">ACTIONS</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filtered.map(lead => {
                    const cfg = TYPE_CONFIG[lead.type] || { color: 'bg-slate-600', icon: Users };
                    const Icon = cfg.icon || Users;
                    const isExpanded = expandedId === lead.id;
                    return (
                      <>
                      <tr key={lead.id + lead.collection}
                        className={`hover:bg-slate-50 transition-colors cursor-pointer ${isExpanded ? 'bg-blue-50/50' : ''}`}
                        onClick={() => setExpandedId(isExpanded ? null : lead.id)}
                        data-testid={`lead-row-${lead.id}`}
                      >
                        <td className="p-3">
                          <Badge className={`${cfg.color} text-white text-[10px] px-2 py-0.5 flex items-center gap-1 w-fit`}>
                            <Icon className="h-3 w-3" />
                            {lead.type}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            {/* Temperature Indicator */}
                            {lead.temperature && TEMP_CONFIG[lead.temperature] && (
                              <span title={`${lead.temperature} lead`}>
                                {(() => {
                                  const TIcon = TEMP_CONFIG[lead.temperature].icon;
                                  return <TIcon className={`h-4 w-4 ${TEMP_CONFIG[lead.temperature].color}`} />;
                                })()}
                              </span>
                            )}
                            <div>
                              <p className="font-semibold text-slate-900 flex items-center gap-1.5">
                                {lead.name || 'Unknown'}
                                {lead.preferred_language === 'es' && (
                                  <span className="text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-bold" title="Spanish-speaking lead" data-testid={`lang-badge-es-${lead.id}`}>ES</span>
                                )}
                              </p>
                              {lead.business && <p className="text-xs text-slate-500 mt-0.5">{lead.business}</p>}
                            </div>
                          </div>
                        </td>
                        <td className="p-3">
                          {lead.email && (
                            <a href={`mailto:${lead.email}`} className="text-xs text-blue-600 hover:underline flex items-center gap-1" onClick={e => e.stopPropagation()}>
                              <Mail className="h-3 w-3" /> {lead.email}
                            </a>
                          )}
                          {lead.phone && (
                            <a href={`tel:${lead.phone}`} className="text-xs text-slate-600 flex items-center gap-1 mt-0.5" onClick={e => e.stopPropagation()}>
                              <Phone className="h-3 w-3" /> {lead.phone}
                            </a>
                          )}
                        </td>
                        <td className="p-3">
                          <span className="text-xs text-slate-500 flex items-center gap-1">
                            <Clock className="h-3 w-3" /> {timeAgo(lead.date)}
                          </span>
                        </td>
                        <td className="p-3" onClick={e => e.stopPropagation()}>
                          <select
                            value={lead.status}
                            onChange={e => updateStatus(lead, e.target.value)}
                            className={`text-xs border rounded-full px-2.5 py-1 font-medium cursor-pointer ${STATUS_COLORS[lead.status] || STATUS_COLORS.new}`}
                            data-testid={`status-select-${lead.id}`}
                          >
                            {STATUS_OPTIONS.map(s => (
                              <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                            ))}
                          </select>
                        </td>
                        <td className="p-3" onClick={e => e.stopPropagation()}>
                          <div className="flex gap-2 items-center">
                            {lead.email && (
                              <a
                                href={`mailto:${lead.email}?subject=Following up on your ${lead.type.toLowerCase()} request&body=Hi ${lead.name || ''},%0D%0A%0D%0AThank you for your interest...`}
                                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                                data-testid={`email-btn-${lead.id}`}
                              >Reply</a>
                            )}
                            {lead.phone && (
                              <a href={`tel:${lead.phone}`} className="text-xs text-emerald-600 hover:text-emerald-800 font-medium">Call</a>
                            )}
                            <button 
                              onClick={(e) => { e.stopPropagation(); setExpandedId(isExpanded ? null : lead.id); }}
                              className="text-xs text-slate-500 hover:text-blue-600 flex items-center gap-0.5"
                            >
                              {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                              {isExpanded ? 'Less' : 'View'}
                            </button>
                          </div>
                        </td>
                      </tr>
                      
                      {/* Expanded Details Row */}
                      {isExpanded && (
                        <tr key={`${lead.id}-details`} className="bg-slate-50 border-b-2 border-blue-200">
                          <td colSpan={6} className="p-4">
                            <div className="grid lg:grid-cols-3 gap-4">
                              {/* Left: Message */}
                              <div>
                                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                                  <MessageSquare className="h-3 w-3" /> Message / Notes
                                </h4>
                                {lead.notes || lead.message ? (
                                  <div className="bg-white border rounded-lg p-3 text-sm text-slate-700 whitespace-pre-wrap max-h-48 overflow-y-auto">
                                    {lead.notes || lead.message}
                                  </div>
                                ) : (
                                  <p className="text-sm text-slate-400 italic">No message provided</p>
                                )}
                                
                                {/* All Details */}
                                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 mt-4">
                                  All Details
                                </h4>
                                <div className="bg-white border rounded-lg p-3 space-y-2 text-sm max-h-48 overflow-y-auto">
                                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                                    <div><span className="text-slate-500">Name:</span> <span className="font-medium text-slate-900">{lead.name || '-'}</span></div>
                                    <div><span className="text-slate-500">Email:</span> <span className="font-medium text-slate-900">{lead.email || '-'}</span></div>
                                    <div><span className="text-slate-500">Phone:</span> <span className="font-medium text-slate-900">{lead.phone || '-'}</span></div>
                                    <div><span className="text-slate-500">Business:</span> <span className="font-medium text-slate-900">{lead.business || '-'}</span></div>
                                    <div><span className="text-slate-500">Type:</span> <span className="font-medium text-slate-900">{lead.type || '-'}</span></div>
                                    <div><span className="text-slate-500">Date:</span> <span className="font-medium text-slate-900">{lead.date ? new Date(lead.date).toLocaleString() : '-'}</span></div>
                                    {lead.website && <div className="col-span-2"><span className="text-slate-500">Website:</span> <a href={lead.website.startsWith('http') ? lead.website : `https://${lead.website}`} target="_blank" rel="noreferrer" className="font-medium text-blue-600 hover:underline">{lead.website}</a></div>}
                                    {lead.source && <div><span className="text-slate-500">Source:</span> <span className="font-medium text-slate-900">{lead.source}</span></div>}
                                    {lead.budget && <div><span className="text-slate-500">Budget:</span> <span className="font-medium text-slate-900">{lead.budget}</span></div>}
                                    {lead.service && <div><span className="text-slate-500">Service:</span> <span className="font-medium text-slate-900">{lead.service}</span></div>}
                                  </div>
                                </div>
                              </div>
                              
                              {/* Middle & Right: CRM Panel */}
                              <div className="lg:col-span-2">
                                <LeadCRMPanel 
                                  lead={lead} 
                                  onUpdate={(updatedLead) => {
                                    setLeads(prev => prev.map(l => l.id === updatedLead.id ? {...l, ...updatedLead} : l));
                                  }}
                                  getAuthHeaders={getAuthHeaders}
                                />
                              </div>
                            </div>
                            
                            {/* Quick Actions */}
                            <div className="flex gap-3 mt-4 pt-3 border-t">
                              <a
                                href={`mailto:${lead.email}?subject=Following up on your inquiry&body=Hi ${lead.name || ''},%0D%0A%0D%0AThank you for reaching out to Web-TechServices. I wanted to follow up on your recent inquiry.%0D%0A%0D%0ABest regards,%0D%0ALarry Scala%0D%0AWeb-TechServices`}
                                className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 flex items-center gap-1"
                                onClick={e => e.stopPropagation()}
                              >
                                <Mail className="h-3 w-3" /> Send Email
                              </a>
                              {lead.phone && (
                                <a
                                  href={`tel:${lead.phone}`}
                                  className="px-3 py-1.5 bg-emerald-600 text-white text-xs rounded-lg hover:bg-emerald-700 flex items-center gap-1"
                                  onClick={e => e.stopPropagation()}
                                >
                                  <Phone className="h-3 w-3" /> Call Now
                                </a>
                              )}
                              <button
                                onClick={(e) => { e.stopPropagation(); updateStatus(lead, 'contacted'); }}
                                className="px-3 py-1.5 bg-purple-600 text-white text-xs rounded-lg hover:bg-purple-700"
                              >
                                Mark Contacted
                              </button>
                              {lead.temperature === 'dead' && (
                                <button
                                  onClick={async (e) => { 
                                    e.stopPropagation(); 
                                    if (window.confirm('Delete this lead permanently?')) {
                                      try {
                                        await axios.delete(`${API}/admin/leads/${lead.collection}/${lead.id}`, { headers: getAuthHeaders() });
                                        setLeads(prev => prev.filter(l => l.id !== lead.id));
                                      } catch (err) {
                                        alert('Failed to delete lead');
                                      }
                                    }
                                  }}
                                  className="px-3 py-1.5 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 flex items-center gap-1"
                                >
                                  <Trash2 className="h-3 w-3" /> Delete Lead
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {!loading && filtered.length > 0 && (
            <div className="px-4 py-2 border-t text-xs text-slate-500 flex justify-between">
              <span>Showing {filtered.length} of {stats.total} leads</span>
              <span>{leads.filter(l => l.status === 'new').length} need follow-up</span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
